import { bind } from "@react-rxjs/core"
import { catchError, defer, filter, map, of, switchMap, tap } from "rxjs"

import {
  EquityErrorType,
  ERROR_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE,
  ERROR_WATCH_LIST_RESPONSE,
  SUCCESS_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE,
  SUCCESS_WATCH_LIST_RESPONSE,
} from "@/generated/TradingGateway"
import { withConnection } from "@/services/withConnection"

import { clearEquitiesError, publishEquitiesError } from "../errorAlert"
import {
  type ErrorGetWatchListsByAccountIdResponse,
  type ErrorWatchListResponse,
  type SuccessGetWatchListsByAccountIdResponse,
  type SuccessWatchListResponse,
  type WatchListId,
  type WatchListInstrument,
  WatchListService,
} from "../TradingGateway"

const publishWatchlistError = (message: string) => {
  publishEquitiesError({ source: "watchlist", message })
}

const getWatchlist$ = defer(() =>
  WatchListService.getWatchListsByAccountId(),
).pipe(
  tap({
    error: (err) => {
      const message = (err instanceof Error ? err.message : String(err)).trim()
      console.error("[watchlist] Hydra stream error:", err)
      publishWatchlistError(`Error fetching watchlist. ${message}`)
    },
  }),
  catchError(() =>
    of({
      type: ERROR_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE,
      payload: {
        errorType: EquityErrorType.InternalError,
        message: "Failed to load watchlists.",
      },
    } as ErrorGetWatchListsByAccountIdResponse),
  ),
  withConnection(),
)

const getWatchlistInstruments$ = getWatchlist$.pipe(
  tap({
    next: (response) => {
      if (response.type === ERROR_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE) {
        const message = response.payload.message
        console.error("[watchlist] Server-returned error:", message)
        publishWatchlistError(message)
      }
    },
  }),
  filter(
    (response): response is SuccessGetWatchListsByAccountIdResponse =>
      response.type === SUCCESS_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE,
  ),
  map((response) => response.payload.map((wl) => wl.id)),
  map((watchlistIds) => watchlistIds[0]),
  filter((id): id is string => !!id),
  switchMap((watchlistId) =>
    defer(() =>
      WatchListService.getWatchListById({
        id: watchlistId,
      } satisfies WatchListId),
    ).pipe(
      tap({
        error: (err) => {
          const message = (
            err instanceof Error ? err.message : String(err)
          ).trim()
          console.error("[watchlist] Hydra stream error:", err)
          publishWatchlistError(
            `Failed to load instruments from watchlist. ${message}`,
          )
        },
      }),
      catchError(() =>
        of({
          type: ERROR_WATCH_LIST_RESPONSE,
          payload: { errorType: EquityErrorType.InternalError, message: "" },
        } as ErrorWatchListResponse),
      ),
    ),
  ),
  tap({
    next: (response) => {
      if (response.type === ERROR_WATCH_LIST_RESPONSE) {
        const message =
          response.payload.message ||
          "Failed to load instruments from watchlist."
        console.error("[watchlist] Server-returned error:", message)
        publishWatchlistError(message)
        return
      }
      clearEquitiesError("watchlist")
    },
  }),
  filter(
    (response): response is SuccessWatchListResponse =>
      response.type === SUCCESS_WATCH_LIST_RESPONSE,
  ),
  map((response) => response.payload.assets),
)

export const [useWatchlistInstruments, watchlistInstruments$] = bind<
  WatchListInstrument[]
>(getWatchlistInstruments$)
